import { Link } from 'react-router-dom'
import { Badge, Avatar, RatingDisplay } from '../ui'
import type { Product, ProductStatus } from '../../types'
import { getPlatformLabel, getCategoryLabelShort } from '../../utils/productLabels'
import './ProductCard.css'

interface ProductCardProps {
  product: Product
}

function getProductStatusBadge(status: ProductStatus): { variant: string; label: string } {
  const statusMap: Record<ProductStatus, { variant: string; label: string }> = {
    DRAFT: { variant: 'gray', label: 'Draft' },
    PENDING_REVIEW: { variant: 'in-progress', label: 'In Review' },
    APPROVED: { variant: 'completed', label: 'Available' },
    REJECTED: { variant: 'cancelled', label: 'Rejected' },
    DELISTED: { variant: 'cancelled', label: 'Delisted' },
  }
  return statusMap[status] || { variant: 'gray', label: status }
}

export function ProductCard({ product }: ProductCardProps) {
  const statusBadge = getProductStatusBadge(product.status)

  return (
    <article className="product-card">
      <Link to={`/products/${product.id}`} className="product-card-link">
        <div className="product-card-header">
          <div className="product-meta">
            <Badge status={statusBadge.variant}>{statusBadge.label}</Badge>
            <Badge variant="blue">{getCategoryLabelShort(product.category)}</Badge>
          </div>
          <span className="usdc-amount">${product.price} USDC</span>
        </div>

        <h3 className="product-title">{product.title}</h3>

        <p className="product-description">
          {product.description.slice(0, 150)}
          {product.description.length > 150 ? '...' : ''}
        </p>

        <div className="product-platform">
          <span className="platform-label">For:</span>
          <span className="platform-value">{getPlatformLabel(product.platform)}</span>
        </div>

        {product.tags.length > 0 && (
          <div className="product-tags">
            {product.tags.slice(0, 3).map((tag) => (
              <span key={tag} className="tag">
                {tag}
              </span>
            ))}
            {product.tags.length > 3 && (
              <span className="tag-more">+{product.tags.length - 3}</span>
            )}
          </div>
        )}

        <div className="product-stats-grid">
          <div className="stat-item">
            <span className="stat-value">{product.totalSales}</span>
            <span className="stat-label">Sales</span>
          </div>
          <div className="stat-item">
            <span className="stat-value">${product.totalRevenue.toFixed(2)}</span>
            <span className="stat-label">Revenue</span>
          </div>
          <div className="stat-item">
            <span className="stat-value">
              {product.stockQuantity === null ? '∞' : product.stockQuantity}
            </span>
            <span className="stat-label">In Stock</span>
          </div>
          <div className="stat-item">
            <span className="stat-value">
              {product.totalReviews > 0 ? (
                <RatingDisplay value={product.averageRating} size="sm" showValue={false} />
              ) : (
                '-'
              )}
            </span>
            <span className="stat-label">
              {product.totalReviews > 0 ? `${product.averageRating.toFixed(1)} (${product.totalReviews})` : 'No reviews'}
            </span>
          </div>
        </div>

        <div className="product-footer">
          <div className="product-seller">
            <Avatar name={product.seller.name} size="sm" />
            <span>{product.seller.name}</span>
          </div>

          {product.hasPurchased && (
            <span className="stat purchased">Purchased</span>
          )}
        </div>
      </Link>
    </article>
  )
}
